import { countries } from "./countries";
import { EmploymentType, Form, Gender, genderLabel, getEmploymentTypes } from "./model";
import { patterns } from "./validation";
export function createTableRow(headers) {
    const defaultValue = (header) => {
        switch (header.type) {
            case "string":
                return "";
            case "number":
                return 0;
            case "date":
                return "";
            case "boolean":
                return false;
            default:
                return "";
        }
    };
    return headers.map((header) => ({
        name: header.name,
        label: header.label,
        type: header.type,
        required: header.required,
        value: defaultValue(header),
    }));
}
function getTableHeaders(employmentType) {
    const sharedEmploymentHeaders = [
        { name: "nameEmployer", label: "Name des Arbeitgebers", type: "string", required: true },
        { name: "addressEmployer", label: "Adresse des Arbeitgebers", type: "string", required: true },
    ];
    switch (employmentType) {
        case EmploymentType.ShortTerm:
            return [
                ...sharedEmploymentHeaders,
                { name: "startDate", label: "Eintrittsdatum", type: "date", required: true },
                { name: "endDate", label: "Austrittsdatum", type: "date", required: true },
                { name: "workingDays", label: "Anzahl Arbeitstage", type: "number", required: true },
            ];
        case EmploymentType.Marginal:
            return [
                ...sharedEmploymentHeaders,
                {
                    name: "additionalMarginalJobStart",
                    label: "Beginn der zusätzlichen Beschäftigung",
                    type: "date",
                    required: true,
                },
                {
                    name: "additionalMarginalJobExemption",
                    label: "RV-Befreiung beantragt",
                    type: "boolean",
                    required: false,
                },
            ];
        case EmploymentType.WorkingStudent:
            return [...sharedEmploymentHeaders];
        case EmploymentType.Regular:
            return [
                ...sharedEmploymentHeaders,
                {
                    name: "requiresSocialSecurityContribution",
                    label: "Sozialversicherungspflichtig",
                    type: "boolean",
                    required: false,
                },
            ];
        case EmploymentType.Trainee:
        case EmploymentType.MidiJob:
        case EmploymentType.Intern:
        case EmploymentType.DualStudent:
        case EmploymentType.Independent:
            return [];
    }
}
export function getOnboardingForm(company) {
    const pvChildHeaders = [
        { name: "firstName", label: "Vorname", type: "string", required: true },
        { name: "lastName", label: "Nachname", type: "string", required: true },
        { name: "birthdate", label: "Geburtsdatum", type: "date", required: true },
    ];
    const countrySpecificPatterns = patterns[company.country] ?? patterns.DE;
    return new Form({
        name: "Personalfragebogen",
        description: "Die folgende Erklärung dient der Beschaffung der erforderlichen Daten zur Lohnabrechnung.",
        requiresEmployeeSignature: true,
        requiresManagerSignature: true,
        considerCaretakerSignatureIfUnderage: true,
        requiredConfirmations: {
            dataProtection: {
                checked: false,
                time: "",
            },
            idCard: {
                checked: false,
                time: "",
            },
            updateInformation: {
                checked: false,
                time: "",
            },
        },
        sections: [
            {
                title: "Beschäftigung",
                fields: [
                    {
                        name: "company",
                        label: "Unternehmen",
                        type: "string",
                        value: company.name,
                        required: true,
                        managerOnly: true,
                    },
                    {
                        name: "venue",
                        label: "Betrieb",
                        type: "string",
                        value: company.venues?.[0]?.name || "",
                        required: true,
                        managerOnly: true,
                        options: company.venues?.length
                            ? company.venues.map((venue) => ({
                                label: venue.name,
                                value: venue.name,
                            }))
                            : undefined,
                    },
                    {
                        name: "employmentType",
                        label: "Beschäftigungsverhältnis",
                        type: "number",
                        value: EmploymentType.Regular,
                        required: true,
                        managerOnly: true,
                        options: getEmploymentTypes().map(({ type, label }) => ({
                            label,
                            value: type,
                        })),
                    },
                    {
                        name: "position",
                        label: "Ausgeübte Tätigkeit",
                        type: "string",
                        value: "",
                        required: true,
                        managerOnly: true,
                        mapsTo: "employeePayrollData.occupation",
                    },
                    {
                        name: "employmentStart",
                        label: "Eintrittsdatum",
                        type: "date",
                        value: "",
                        required: true,
                        managerOnly: true,
                    },
                    {
                        name: "employmentEnd",
                        label: "Austrittsdatum",
                        description: "Bei unbefristetem Vertrag leer lassen.",
                        type: "date",
                        value: "",
                        required: false,
                        managerOnly: true,
                    },
                ],
            },
            {
                title: "Vereinbarte Vergütung",
                fields: [
                    {
                        name: "paymentType",
                        label: "Art der Vergütung",
                        type: "string",
                        value: "",
                        required: true,
                        options: [
                            { label: "Monatliches Gehalt", value: "monthly" },
                            { label: "Stundenlohn", value: "hourly" },
                        ],
                        managerOnly: true,
                    },
                    {
                        name: "monthlySalary",
                        label: "Gehalt (€)",
                        type: "number",
                        step: "0.01",
                        required: true,
                        value: "",
                        condition: { field: "paymentType", value: "monthly" },
                        managerOnly: true,
                    },
                    {
                        name: "hourlyRate",
                        label: "Stundenlohn (€)",
                        type: "number",
                        step: "0.01",
                        required: true,
                        value: "",
                        condition: { field: "paymentType", value: "hourly" },
                        managerOnly: true,
                    },
                ],
            },
            {
                title: "Vereinbarte Arbeitszeit",
                fields: [
                    {
                        name: "hoursPerWeek",
                        label: "Stunden / Woche",
                        step: "0.001",
                        type: "number",
                        required: true,
                        value: "",
                        managerOnly: true,
                    },
                ],
            },
            {
                title: "Persönliche Angaben",
                fields: [
                    {
                        name: "title",
                        label: "Titel",
                        type: "string",
                        value: "",
                        required: false,
                        mapsTo: "employee.title",
                    },
                    {
                        name: "firstName",
                        label: "Vorname",
                        type: "string",
                        value: "",
                        required: true,
                        mapsTo: "employee.firstName",
                    },
                    {
                        name: "lastName",
                        label: "Nachname",
                        type: "string",
                        value: "",
                        required: true,
                        mapsTo: "employee.lastName",
                    },
                    {
                        name: "familyStatus",
                        label: "Familienstand",
                        type: "string",
                        value: "",
                        required: true,
                        options: [
                            { label: "Ledig", value: "single" },
                            { label: "Verheiratet", value: "married" },
                            { label: "Verwitwet", value: "widowed" },
                            { label: "Geschieden", value: "divorced" },
                            { label: "Eingetragene Lebenspartnerschaft", value: "registeredLifePartnership" },
                            {
                                label: "Eingetragene Lebenspartnerschaft verstorben",
                                value: "registeredLifePartnershipDeceased",
                            },
                            {
                                label: "Eingetragene Lebenspartnerschaft aufgehoben",
                                value: "registeredLifePartnershipCancelled",
                            },
                        ],
                        mapsTo: "employeePayrollData.familyStatus",
                    },
                    {
                        name: "address",
                        label: "Adresse",
                        type: "string",
                        value: "",
                        required: true,
                        mapsTo: "employee.address",
                    },
                    {
                        name: "postalCode",
                        label: "Postleitzahl",
                        type: "string",
                        value: "",
                        required: true,
                        mapsTo: "employee.postalCode",
                        pattern: countrySpecificPatterns.postalCode,
                    },
                    {
                        name: "city",
                        label: "Ort",
                        type: "string",
                        value: "",
                        required: true,
                        mapsTo: "employee.city",
                    },
                    {
                        name: "phone",
                        label: "Telefon",
                        type: "string",
                        value: "",
                        required: true,
                        mapsTo: "employee.phone",
                    },
                    {
                        name: "email",
                        label: "Email",
                        type: "string",
                        value: "",
                        required: true,
                        mapsTo: "employee.email",
                    },
                    {
                        name: "birthday",
                        label: "Geburtstag",
                        type: "date",
                        value: "",
                        required: true,
                        mapsTo: "employee.birthday",
                    },
                    {
                        name: "gender",
                        label: "Geschlecht",
                        type: "string",
                        value: "",
                        required: true,
                        options: Object.values(Gender).map((gender) => ({
                            label: genderLabel(gender),
                            value: gender,
                        })),
                        mapsTo: "employee.gender",
                    },
                    {
                        name: "birthName",
                        label: "Geburtsname",
                        type: "string",
                        value: "",
                        required: false,
                        description: "(Falls Abweichend)",
                        mapsTo: "employee.birthName",
                    },
                    {
                        name: "birthCity",
                        label: "Geburtsort",
                        type: "string",
                        value: "",
                        required: true,
                        mapsTo: "employee.birthCity",
                    },
                    {
                        name: "birthCountry",
                        label: "Geburtsland",
                        type: "string",
                        value: "DE",
                        required: true,
                        options: countries.map((c) => ({
                            label: c.name,
                            value: c.iso,
                        })),
                        mapsTo: "employee.birthCountry",
                    },
                    {
                        name: "nationality",
                        label: "Nationalität",
                        type: "string",
                        value: "DE",
                        required: true,
                        options: countries.map((c) => ({
                            label: c.name,
                            value: c.iso,
                        })),
                        mapsTo: "employee.nationality",
                    },
                ],
            },
            {
                title: "Steuerliche Angaben",
                fields: [
                    {
                        name: "taxId",
                        label: "Steuer-ID",
                        description: "Bestehend aus elf Ziffern, keine Buchstaben.",
                        type: "string",
                        pattern: countrySpecificPatterns.taxId.pattern,
                        value: "",
                        required: true,
                        mapsTo: "employee.taxId",
                    },
                    {
                        name: "socialSecurityNumber",
                        label: "SV-Nummer",
                        description: 'Auch "Rentenversicherungsnummer (RVNR)" - Beispiel: 15 070649 C 103',
                        type: "string",
                        pattern: countrySpecificPatterns.socialSecurityNumber.pattern,
                        value: "",
                        required: true,
                        mapsTo: "employee.socialSecurityNumber",
                    },
                    {
                        name: "passportNumber",
                        label: "Ausweis- oder Reisepassnummer",
                        description: "Oben rechts auf dem Personalausweis",
                        type: "string",
                        value: "",
                        required: false,
                        mapsTo: "employeePayrollData.passportNumber",
                    },
                    {
                        name: "mainJob",
                        label: "Diese Beschäftigung ist meine Hauptbeschäftigung",
                        type: "boolean",
                        value: false,
                        required: false,
                        mapsTo: "employeePayrollData.mainJob",
                    },
                    {
                        name: "pvBonusChildren",
                        label: "Berücksichtigungsfähige Kinder für PV- Zuschlag",
                        type: "string",
                        value: "",
                        options: [
                            { label: "Ja", value: "yes" },
                            { label: "Nein", value: "nein" },
                            { label: "Unbestimmt", value: "unbestimmt" },
                        ],
                        required: true,
                        mapsTo: "employeePayrollData.pvBonusChildren",
                    },
                    {
                        name: "pvChildInfo",
                        type: "table",
                        label: "",
                        description: "Der zu zahlende Beitrag richtet sich nach der Anzahl der Kinder unter 25 Jahren.",
                        condition: { field: "pvBonusChildren", value: "yes" },
                        max: "5",
                        headers: pvChildHeaders,
                        data: [createTableRow(pvChildHeaders)],
                        mapsTo: "employeePayrollData.pvChildInfo",
                    },
                    {
                        name: "taxClass",
                        label: "Steuerklasse",
                        type: "string",
                        value: "",
                        options: [
                            { label: "Steuerklasse 1", value: "1" },
                            { label: "Steuerklasse 2", value: "2" },
                            { label: "Steuerklasse 3", value: "3" },
                            { label: "Steuerklasse 4", value: "4" },
                            { label: "Steuerklasse 5", value: "5" },
                            { label: "Steuerklasse 6", value: "6" },
                        ],
                        required: true,
                        mapsTo: "employeePayrollData.taxClass",
                    },
                    {
                        name: "taxClass4Factor",
                        label: "Faktor für Steuerklasse 4",
                        type: "number",
                        value: "",
                        step: "0.01",
                        required: true,
                        condition: { field: "taxClass", value: "4" },
                        mapsTo: "employeePayrollData.taxClass4Factor",
                    },
                    {
                        name: "childrenFreeAmount",
                        label: "Kinderfreibetrag",
                        type: "string",
                        value: "",
                        required: false,
                        mapsTo: "employeePayrollData.childrenFreeAmount",
                    },
                    {
                        name: "confession",
                        label: "Konfession",
                        type: "string",
                        value: "",
                        required: false,
                        options: [
                            { label: "Keine", value: "" },
                            { label: "Römisch-Katholisch", value: "catholic" },
                            { label: "Evangelisch", value: "evangelical" },
                            { label: "Andere", value: "other" },
                        ],
                        mapsTo: "employeePayrollData.confession",
                    },
                ],
            },
            {
                title: "Bankverbindung",
                fields: [
                    {
                        name: "payByBankTransfer",
                        label: "Zahlung per Banküberweisung",
                        type: "boolean",
                        required: false,
                        value: true,
                        mapsTo: "employeePayrollData.bankDetails.payByBankTransfer",
                    },
                ],
            },
            {
                condition: {
                    field: "payByBankTransfer",
                    value: true,
                },
                fields: [
                    {
                        name: "bankAccountOwner",
                        label: "Kontoinhaber",
                        type: "string",
                        required: true,
                        value: "",
                        mapsTo: "employeePayrollData.bankDetails.bankAccountOwner",
                    },
                    {
                        name: "iban",
                        label: "IBAN",
                        type: "string",
                        required: true,
                        value: "",
                        mapsTo: "employeePayrollData.bankDetails.iban",
                    },
                    {
                        name: "bankName",
                        label: "Name der Bank",
                        type: "string",
                        required: true,
                        value: "",
                        mapsTo: "employeePayrollData.bankDetails.bankName",
                    },
                ],
            },
            {
                title: "Aktueller Status",
                condition: {
                    field: "employmentType",
                    value: [EmploymentType.WorkingStudent, EmploymentType.Marginal, EmploymentType.ShortTerm],
                },
                fields: [
                    {
                        name: "immatriculationStatus",
                        label: "Sie sind derzeit immatrikuliert",
                        description: "Sie sind an einer Hochschule, Fachhochschule oder Universität eingeschrieben",
                        type: "boolean",
                        required: false,
                        value: false,
                        condition: { field: "employmentType", value: EmploymentType.WorkingStudent },
                        mapsTo: "employeePayrollData.currentStatus.immatriculationStatus",
                    },
                    {
                        name: "currentStatus",
                        label: "Aktueller Status",
                        type: "string",
                        required: true,
                        value: "",
                        mapsTo: "employeePayrollData.currentStatus.currentStatus",
                        options: [
                            { label: "Arbeitnehmer:in mit Hauptbeschäftigung", value: "mainEmployment" },
                            { label: "Unbezahlter Urlaub (Hauptbeschäftigung)", value: "unpaidLeave" },
                            { label: "Elternzeit (Hauptbeschäftigung)", value: "parentalLeave" },
                            { label: "Schüler:in", value: "pupil" },
                            { label: "Student:in (Immatrikulationsbescheinigung)", value: "student" },
                            {
                                label: "Schulentlassener:in mit Berufsausbildungsabsicht",
                                value: "schoolLeaverApprenticeship",
                            },
                            { label: "Schulentlassene:r mit Studienabsicht", value: "schoolLeaverStudy" },
                            {
                                label: "Schulentlassene:r mit Freiwilligendienstabsicht",
                                value: "schoolLeaverVoluntary",
                            },
                            { label: "Arbeit-/Ausbildungssuchende:r", value: "jobSeeker" },
                            { label: "Freiwilligendienstleistende:r", value: "voluntaryService" },
                            { label: "Pflichtpraktikant:in", value: "mandatoryIntern" },
                            { label: "Freiwillige:r Praktikant:in", value: "voluntaryIntern" },
                            { label: "Beamte:r", value: "civilServant" },
                            { label: "Selbstständig", value: "selfEmployed" },
                            { label: "Altersvollrentner:in (vor Regelaltersgrenze)", value: "retireeBeforeAgeLimit" },
                            { label: "Altersvollrentner:in (nach Regelaltersgrenze)", value: "retireeAfterAgeLimit" },
                            { label: "Versorgungsempfänger:in (nach Altersgrenze)", value: "pensioner" },
                            { label: "Sonstiges", value: "other" },
                        ],
                        condition: {
                            field: "employmentType",
                            value: [EmploymentType.WorkingStudent, EmploymentType.Marginal, EmploymentType.ShortTerm],
                        },
                    },
                    {
                        name: "otherStatus",
                        label: "Sonstiges",
                        type: "string",
                        required: false,
                        value: "",
                        mapsTo: "employeePayrollData.currentStatus.otherStatus",
                        condition: {
                            field: "currentStatus",
                            value: "other",
                        },
                    },
                    {
                        name: "registeredAsJobSeeker",
                        type: "boolean",
                        label: "Arbeitssuchend",
                        description: "Sie sind zu Beginn des Beschäftigungsverhältnisses als arbeits- oder ausbildungssuchend bei der Agentur für Arbeit gemeldet.",
                        required: false,
                        value: false,
                        mapsTo: "employeePayrollData.currentStatus.registeredAsJobSeeker",
                        condition: {
                            field: "employmentType",
                            value: [EmploymentType.Marginal, EmploymentType.ShortTerm],
                        },
                    },
                    {
                        name: "responsibleAgency",
                        type: "string",
                        label: "Agentur für Arbeit in ",
                        required: true,
                        value: "",
                        mapsTo: "employeePayrollData.currentStatus.responsibleAgency",
                        condition: { field: "registeredAsJobSeeker", value: true },
                    },
                    {
                        name: "receivingAgencyBenefits",
                        type: "boolean",
                        label: "mit Leistungsbezug",
                        required: false,
                        value: false,
                        mapsTo: "employeePayrollData.currentStatus.receivingAgencyBenefits",
                        condition: { field: "registeredAsJobSeeker", value: true },
                    },
                ],
            },
            {
                title: "Sachverhalte",
                description: "Bitte kreuzen Sie alle Sachverhalte an, die für Sie zutreffen:",
                condition: { field: "employmentType", value: EmploymentType.WorkingStudent },
                fields: [
                    {
                        name: "lowSalary",
                        label: "Gehalt",
                        description: "Sie werden unter 556€ brutto im Monat verdienen",
                        type: "boolean",
                        required: false,
                        value: false,
                        mapsTo: "employeePayrollData.workingStudentFacts.lowSalary",
                    },
                    {
                        name: "shortTermEmployment",
                        label: "Kurzfristige Beschäftigung",
                        description: "Die Beschäftigung ist von vorne herein auf 70 Arbeitseinsätze befristet.",
                        type: "boolean",
                        required: false,
                        value: false,
                        mapsTo: "employeePayrollData.workingStudentFacts.shortTermEmployment",
                    },
                    {
                        name: "moreThan20hOutsideSemesterBreak",
                        label: "Über 20h außerhalb der Semesterferien",
                        description: "Die wöchentliche Arbeitszeit beträgt außerhalb der Semesterferien mehr als 20 Stunden.",
                        type: "boolean",
                        required: false,
                        value: false,
                        mapsTo: "employeePayrollData.workingStudentFacts.moreThan20hOutsideSemesterBreak",
                    },
                    {
                        name: "vacationSemester",
                        label: "Urlaubssemester",
                        description: "Sie sind im Urlaubssemester.",
                        type: "boolean",
                        required: false,
                        value: false,
                        mapsTo: "employeePayrollData.workingStudentFacts.vacationSemester",
                    },
                    {
                        name: "semesterAbove25",
                        label: "26. Fachsemester oder höher",
                        description: "Sie sind im 26. Fachsemester oder höher.",
                        type: "boolean",
                        required: false,
                        value: false,
                        mapsTo: "employeePayrollData.workingStudentFacts.semesterAbove25",
                    },
                    {
                        name: "moreThan182DaysOver20h",
                        label: "Mehr als 182 Tage mit >20h/Woche gearbeitet",
                        description: "Sie haben im letzten Jahr an mehr als 182 Kalendertagen eine Beschäftigung mit einer wöchentlichen Arbeitszeit von mehr als 20 Stunden ausgeübt.",
                        type: "boolean",
                        required: false,
                        value: false,
                        mapsTo: "employeePayrollData.workingStudentFacts.moreThan182DaysOver20h",
                    },
                ],
            },
            {
                title: "Angaben zur Berufsmäßigkeit",
                description: 'Sollte die monatliche Vergütung mindestens einmalig über 556€ liegen, muss die sogenannte "Berufsmäßigkeit" überprüft werden. Bitte kreuzen Sie alle Sachverhalte an, die für Sie zutreffen.',
                condition: { field: "employmentType", value: EmploymentType.ShortTerm },
                fields: [
                    {
                        name: "registeredAsJobSeekerShortTerm",
                        label: "Arbeitssuchend",
                        description: "Sie sind als arbeitssuchend bei der Agentur für Arbeit gemeldet.",
                        type: "boolean",
                        required: false,
                        value: false,
                        mapsTo: "employeePayrollData.shortTermEmploymentProfessionalism.registeredAsJobSeekerShortTerm",
                    },
                    {
                        name: "receivingAgencyBenefits",
                        label: "Arbeitslosengeld",
                        description: "Bezug von Leistungen der Arbeitsagentur (z. B. Arbeitslosengeld)",
                        type: "boolean",
                        required: false,
                        value: false,
                        mapsTo: "employeePayrollData.shortTermEmploymentProfessionalism.receivingAgencyBenefits",
                    },
                    {
                        name: "receivingSocialBenefits",
                        label: "Sozialhilfe",
                        description: "Bezug von ALG II, Sozialgeld oder Sozialhilfe",
                        type: "boolean",
                        required: false,
                        value: false,
                        mapsTo: "employeePayrollData.shortTermEmploymentProfessionalism.receivingSocialBenefits",
                    },
                    {
                        name: "parentalLeave",
                        label: "Elternzeit",
                        description: "Sie befinden sich in Elternzeit",
                        type: "boolean",
                        required: false,
                        value: false,
                        mapsTo: "employeePayrollData.shortTermEmploymentProfessionalism.parentalLeave",
                    },
                    {
                        name: "militaryOrVoluntaryService",
                        label: "Schule - Wehrdienst",
                        description: "Beschäftigung zwischen Schulabschluss und Wehrdienst, Zeitsoldat, Jugendfreiwilligendienst (FSJ/FÖJ)",
                        type: "boolean",
                        required: false,
                        value: false,
                        mapsTo: "employeePayrollData.shortTermEmploymentProfessionalism.militaryOrVoluntaryService",
                    },
                    {
                        name: "federalVoluntaryService",
                        label: "Schule - Bundesfreiwilligendienst",
                        description: "Beschäftigung zwischen Schulabschluss und Bundesfreiwilligendienst",
                        type: "boolean",
                        required: false,
                        value: false,
                        mapsTo: "employeePayrollData.shortTermEmploymentProfessionalism.federalVoluntaryService",
                    },
                    {
                        name: "startingApprenticeship",
                        label: "Schule - Ausbildung",
                        description: "Beschäftigung zwischen Schulabschluss und Beginn einer Ausbildung oder Dauerbeschäftigung als Arbeitnehmer",
                        type: "boolean",
                        required: false,
                        value: false,
                        mapsTo: "employeePayrollData.shortTermEmploymentProfessionalism.startingApprenticeship",
                    },
                    {
                        name: "startingPublicService",
                        label: "Schule - Beamtenstart",
                        description: "Beschäftigung zwischen Schulabschluss und Beginn eines beamtenähnlichen Dienstverhältnisses",
                        type: "boolean",
                        required: false,
                        value: false,
                        mapsTo: "employeePayrollData.shortTermEmploymentProfessionalism.startingPublicService",
                    },
                    {
                        name: "employmentBetweenTrainingAndStudy",
                        label: "Ausbildung - Studium",
                        description: "Beschäftigung zwischen Abschluss der Ausbildung und Studienbeginn",
                        type: "boolean",
                        required: false,
                        value: false,
                        mapsTo: "employeePayrollData.shortTermEmploymentProfessionalism.employmentBetweenTrainingAndStudy",
                    },
                    {
                        name: "employmentBetweenStudyAndWork",
                        label: "Studium - Job",
                        description: "Beschäftigung zwischen Studienabschluss und dem Eintritt ins Berufsleben",
                        type: "boolean",
                        required: false,
                        value: false,
                        mapsTo: "employeePayrollData.shortTermEmploymentProfessionalism.employmentBetweenStudyAndWork",
                    },
                    {
                        name: "employmentDuringService",
                        label: "Neben Wehrdienst",
                        description: "Beschäftigung während des freiwilligen Wehrdienstes",
                        type: "boolean",
                        required: false,
                        value: false,
                        mapsTo: "employeePayrollData.shortTermEmploymentProfessionalism.employmentDuringService",
                    },
                    {
                        name: "recentFixedTermContract",
                        label: "Kurzzeitvertrag",
                        description: "Beschäftigung innerhalb eines kürzlich beendeten Rahmenarbeitsvertrags (max. 1 Jahr, max. 2 Monate zurückliegend)",
                        type: "boolean",
                        required: false,
                        value: false,
                        mapsTo: "employeePayrollData.shortTermEmploymentProfessionalism.recentFixedTermContract",
                    },
                    {
                        name: "sameEmployerBefore",
                        label: "Gleicher Arbeitgeber",
                        description: "Unmittelbar zuvor beim selben Arbeitgeber beschäftigt",
                        type: "boolean",
                        required: false,
                        value: false,
                        mapsTo: "employeePayrollData.shortTermEmploymentProfessionalism.sameEmployerBefore",
                    },
                    {
                        name: "newEmploymentAfterPrevious",
                        label: "Anschluss - Job",
                        description: "Beschäftigung direkt nach einer anderen Tätigkeit mit Versicherungspflicht bei einem anderen Arbeitgeber",
                        type: "boolean",
                        required: false,
                        value: false,
                        mapsTo: "employeePayrollData.shortTermEmploymentProfessionalism.newEmploymentAfterPrevious",
                    },
                    {
                        name: "leaveFromMainJob",
                        label: "Urlaub",
                        description: "Paralleler Urlaub (bezahlt oder unbezahlt) in der Hauptbeschäftigung",
                        type: "boolean",
                        required: false,
                        value: false,
                        mapsTo: "employeePayrollData.shortTermEmploymentProfessionalism.leaveFromMainJob",
                    },
                    {
                        name: "eveningSchoolStudent",
                        label: "Abendschule",
                        description: "Sie besuchen außerhalb der Arbeitszeit eine Abendschule oder allgemeinbildende Schule",
                        type: "boolean",
                        required: false,
                        value: false,
                        mapsTo: "employeePayrollData.shortTermEmploymentProfessionalism.eveningSchoolStudent",
                    },
                    {
                        name: "seasonalHarvestWork",
                        label: "Erntehilfe",
                        description: "Zeitlich begrenzte saisonale Arbeit als Erntehelfer im Herkunftsland",
                        type: "boolean",
                        required: false,
                        value: false,
                        mapsTo: "employeePayrollData.shortTermEmploymentProfessionalism.seasonalHarvestWork",
                    },
                ],
            },
            {
                title: "Zusätzliche Beschäftigungen",
                condition: {
                    field: "employmentType",
                    value: [EmploymentType.ShortTerm, EmploymentType.Marginal, EmploymentType.WorkingStudent],
                },
                fields: [
                    {
                        name: "additionalRegularEmployment",
                        label: "Besteht eine zusätzliche Hauptbeschäftigung?",
                        description: "Sie haben eine zusätzliche Beschäftigung durch die eine Sozialversicherungspflicht entsteht.",
                        type: "boolean",
                        required: false,
                        value: false,
                        mapsTo: "employeePayrollData.additionalEmployment.additionalRegularEmployment",
                        condition: {
                            field: "employmentType",
                            value: [EmploymentType.ShortTerm, EmploymentType.Marginal],
                        },
                    },
                    {
                        name: "additionalRegularEmploymentInfo",
                        type: "table",
                        label: "",
                        condition: { field: "additionalRegularEmployment", value: true },
                        headers: getTableHeaders(EmploymentType.Regular),
                        data: [createTableRow(getTableHeaders(EmploymentType.Regular))],
                        mapsTo: "employeePayrollData.additionalEmployment.additionalRegularEmploymentInfo",
                    },
                    {
                        name: "additionalWorkingStudentEmployment",
                        label: "Besteht eine zusätzliche Beschäftigung als Werkstudent?",
                        description: "Alle Beschäftigungen müssen zusammengerechnet werden und dürfen die Zeitgrenze nicht überschreiten.",
                        type: "boolean",
                        required: false,
                        value: false,
                        mapsTo: "employeePayrollData.additionalEmployment.additionalWorkingStudentEmployment",
                        condition: {
                            field: "employmentType",
                            value: [EmploymentType.WorkingStudent],
                        },
                    },
                    {
                        name: "additionalWorkingStudentEmploymentInfo",
                        type: "table",
                        label: "",
                        description: "Tragen Sie hier alle Beschäftigungen als Werkstudent ein, die Sie aktuell ausüben.",
                        condition: { field: "additionalWorkingStudentEmployment", value: true },
                        headers: getTableHeaders(EmploymentType.WorkingStudent),
                        data: [createTableRow(getTableHeaders(EmploymentType.WorkingStudent))],
                        mapsTo: "employeePayrollData.additionalEmployment.additionalWorkingStudentEmploymentInfo",
                    },
                    {
                        name: "additionalMarginalEmployment",
                        label: "Besteht eine zusätzliche Beschäftigung als Aushilfe / Minijob?",
                        type: "boolean",
                        required: false,
                        value: false,
                        mapsTo: "employeePayrollData.additionalEmployment.additionalMarginalEmployment",
                        condition: {
                            field: "employmentType",
                            value: [EmploymentType.Marginal],
                        },
                    },
                    {
                        name: "additionalMarginalEmploymentInfo",
                        type: "table",
                        label: "",
                        description: "Tragen Sie hier alle Beschäftigungen ein, die Sie aktuell im Rahmen der Aushilfe-/Minijob-Beschäftigung ausüben.",
                        condition: { field: "additionalMarginalEmployment", value: true },
                        headers: getTableHeaders(EmploymentType.Marginal),
                        data: [createTableRow(getTableHeaders(EmploymentType.Marginal))],
                        mapsTo: "employeePayrollData.additionalEmployment.additionalMarginalEmploymentInfo",
                    },
                    {
                        name: "additionalMarginalEmploymentSalary",
                        label: "Monatliches Gehalt",
                        description: "Durchschnittliches Gehalt aller Beschäftigungen als Aushilfe der letzten 12 Monate",
                        type: "number",
                        required: true,
                        value: "",
                        condition: { field: "additionalMarginalEmployment", value: true },
                    },
                    {
                        name: "additionalShortTermEmployment",
                        label: "Bestanden weitere befristete Beschäftigungsverhältnisse?",
                        type: "boolean",
                        value: false,
                        mapsTo: "employeePayrollData.additionalEmployment.additionalShortTermEmployment",
                        condition: {
                            field: "employmentType",
                            value: [EmploymentType.ShortTerm],
                        },
                    },
                    {
                        name: "additionalShortTermEmploymentInfo",
                        type: "table",
                        label: "",
                        description: "Alle befristeten Beschäftigungen im Kalenderjahr (auch anteilig) dürfen in Summe die 70 Arbeitstage nicht überschreiten.",
                        condition: { field: "additionalShortTermEmployment", value: true },
                        headers: getTableHeaders(EmploymentType.ShortTerm),
                        data: [createTableRow(getTableHeaders(EmploymentType.ShortTerm))],
                        mapsTo: "employeePayrollData.additionalEmployment.additionalShortTermEmploymentInfo",
                    },
                ],
            },
            {
                title: "Ausbildung",
                fields: [
                    {
                        name: "schoolEducation",
                        label: "Höchster Schulabschluss",
                        type: "string",
                        required: true,
                        value: "",
                        mapsTo: "employeePayrollData.education.schoolEducation",
                        options: [
                            { label: "ohne Schulabschluss", value: "none" },
                            { label: "Haupt - /Volksschulabschluss", value: "primary" },
                            { label: "Mittlere Reife oder gleichwert. Abschluss", value: "secondary" },
                            { label: "Abitur / Fachabitur", value: "higher" },
                        ],
                    },
                    {
                        name: "professionalEducation",
                        label: "Höchste berufliche Ausbildung",
                        type: "string",
                        required: true,
                        value: "",
                        mapsTo: "employeePayrollData.education.professionalEducation",
                        options: [
                            { label: "ohne beruflichen Abschluss", value: "none" },
                            { label: "Abschluss einer anerkannten Berufsausbildung", value: "apprenticeship" },
                            { label: "Meister-/Techniker oder gleichw. Fachschulabschluss", value: "technician" },
                            { label: "Bachelor", value: "bachelor" },
                            { label: "Diplom/Magister/Master/Staatsexamen", value: "diploma/master" },
                            { label: "Promotion", value: "doctoral" },
                        ],
                    },
                ],
            },
            {
                title: "Krankenversicherungsstatus",
                fields: [
                    {
                        name: "ensuranceType",
                        label: "Art der Versicherung",
                        type: "string",
                        required: true,
                        value: "",
                        mapsTo: "employeePayrollData.healthInsuranceStatus.ensuranceType",
                        options: [
                            { label: "Gesetzlich", value: "statutory" },
                            { label: "Privat", value: "private" },
                            { label: "Im Ausland versichert", value: "abroad" },
                            { label: "Sonstiges", value: "other" },
                        ],
                    },
                    {
                        name: "otherEnsuranceType",
                        label: "Sonstige Versicherung",
                        type: "string",
                        required: true,
                        value: "",
                        mapsTo: "employeePayrollData.healthInsuranceStatus.otherEnsuranceType",
                        condition: { field: "ensuranceType", value: "other" },
                    },
                    {
                        name: "lastEnsurance",
                        label: "Letzte gesetzliche Versicherung",
                        type: "string",
                        description: "Für die Abführung der RV-Beiträge",
                        required: false,
                        value: "",
                        mapsTo: "employeePayrollData.healthInsuranceStatus.lastEnsurance",
                        condition: { field: "ensuranceType", value: "private" },
                    },
                    {
                        name: "ensurerName",
                        label: "Name der Krankenkasse",
                        type: "string",
                        required: true,
                        value: "",
                        mapsTo: "employeePayrollData.healthInsuranceStatus.ensurerName",
                    },
                ],
            },
            {
                title: "Schwerbehinderung",
                fields: [
                    {
                        name: "disabilityLevel",
                        label: "Grad der Behinderung angeben",
                        description: "Freiwillige Angabe",
                        type: "boolean",
                        required: false,
                        value: false,
                        mapsTo: "employeePayrollData.disabilityInfo.disabilityLevel",
                    },
                    {
                        name: "disabilityLevelPercentage",
                        label: "Grad der Behinderung",
                        description: "Schwerbehinderung in % (20-100)",
                        type: "number",
                        required: true,
                        value: "",
                        mapsTo: "employeePayrollData.disabilityInfo.disabilityLevelPercentage",
                        condition: { field: "disabilityLevel", value: true },
                    },
                ],
            },
        ],
    });
}
export function getDefaultForms(company) {
    return company ? [getOnboardingForm(company)] : [];
}
export const FORM_CONFIRMATION_TEXTS = {
    dataProtection: {
        title: "Datenschutz",
        description: "Meine personenbezogenen Daten dürfen zum Zweck der Entgeltabrechnung, der Dienstplanung, der Zeiterfassung und der Bereitstellung meiner Lohnunterlagen gespeichert und verarbeitet werden. Meine Daten dürfen auf Basis der gesetzlichen Bestimmungen dem Finanzamt, der Arbeitsagentur und den Sozialversicherungsträgern übertragen werden und mir in Form eines persönlichen online Personalportals zur Verfügung gestellt werden. Die oben angegebene Email darf für diese Zwecke als mein persönlicher Login genutzt werden. Mir ist bekannt, dass meine Zustimmung jederzeit widerrufen kann, insbesondere kann ich der elektronischen Übermittlung von Arbeits- und Nebeneinkommensbescheinigungen (Bea) an die Bundesagentur für Arbeit jederzeit widersprechen.",
        confirmText: "Ich stimme zu",
    },
    idCard: {
        title: "Ausweispflicht",
        description: "Auf die gesetzliche notwendige Mitführung des Personalausweises oder Passes während meiner Beschäftigung wurde ich hingewiesen.",
        confirmText: "Ich stimme zu",
    },
    updateInformation: {
        title: "Mitteilungspflicht bzgl. Änderungen",
        description: "Ich versichere, dass die vorstehenden Angaben der Wahrheit entsprechen. Auf die für mich bestehende Pflicht zum Mitwirken und zur Auskunft gegenüber meinem Arbeitgeber wurde ich hingewiesen. Sollten – auch künftig – Änderungen bei den hier gemachten Angaben eintreten, so werde ich meinen Arbeitgeber unverzüglich schriftlich darüber informieren.",
        confirmText: "Ich stimme zu",
    },
};
//# sourceMappingURL=forms.js.map